import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ChevronDown, Sparkles } from "lucide-react";
import { MaskLine } from "./Reveal";
import { useLead } from "../lib/store";
import { track } from "../lib/track";
import { WhatsAppIcon } from "./icons";

const HERO_IMG =
  "https://images.unsplash.com/photo-1580514120049-74dd27aa9262?crop=entropy&cs=srgb&fm=jpg&q=80&auto=format&fit=crop&w=2000";

const TRUST = [
  "Professional Travel Service",
  "Custom Itinerary",
  "Hotel & Transport",
  "Local Support",
  "Group & Private Trip",
];

export default function KineticHero() {
  const ref = useRef(null);
  const { openLead } = useLead();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden bg-tosca-900">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Destinasi perjalanan internasional"
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/35" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="container-x relative z-10 pb-16 pt-40 md:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="chip glass-dark text-white"
        >
          <Sparkles className="h-3.5 w-3.5 text-gold" /> BEYOND TRAVEL — LAND ARRANGEMENT INTERNASIONAL
        </motion.p>

        <h1 className="mt-6 max-w-5xl font-heading text-3xl font-black leading-[1.04] tracking-tight text-white sm:text-6xl sm:leading-[1.02] md:text-7xl xl:text-8xl">
          <MaskLine delay={0.25}>Explore The World,</MaskLine>
          <MaskLine delay={0.42}>
            We Handle <span className="text-gold-gradient italic">The Journey.</span>
          </MaskLine>
        </h1>

        <MaskLine delay={0.62}>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/85 sm:text-base">
            Land Arrangement internasional yang dirancang untuk membuat perjalanan Anda lebih mudah, nyaman, dan terorganisir.
          </p>
        </MaskLine>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-8 flex flex-col gap-3 sm:flex-row"
        >
          <Link to="/destinations" className="btn btn-gold !px-8 !py-3.5" data-testid="hero-cta-explore-destinations">
            Explore Destinations
          </Link>
          <button
            onClick={() => {
              track("click_whatsapp", { source: "hero" });
              openLead();
            }}
            className="btn btn-wa !px-8 !py-3.5"
            data-testid="hero-cta-whatsapp"
          >
            <WhatsAppIcon className="h-5 w-5" /> Konsultasi via WhatsApp
          </button>
        </motion.div>

        <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15, duration: 0.8 }} className="mt-10 flex flex-wrap gap-2">
          {TRUST.map((t) => (
            <li key={t} className="chip glass-dark text-white/90">
              <Check className="h-3.5 w-3.5 text-gold" /> {t}
            </li>
          ))}
        </motion.ul>
      </motion.div>

      <motion.div style={{ opacity: fade }} className="pointer-events-none absolute bottom-6 right-6 hidden md:block">
        <ChevronDown className="h-6 w-6 animate-bounce text-white/60" />
      </motion.div>
    </section>
  );
}
